import React, { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import {
  fetchPlatformPaymentMethods,
  updatePlatformPaymentMethods,
  type ManagedPaymentMethod,
} from "../../api/paymentMethods";

const METHOD_COPY: Record<
  ManagedPaymentMethod,
  { title: string; hint: string }
> = {
  stripe: {
    title: "Card (Stripe)",
    hint: "Checkout and subscription items on the existing MayorX Stripe customer.",
  },
  ves: {
    title: "Bolívares (VES)",
    hint: "Bank transfer + receipt. Requests land in VES Payments for approval.",
  },
};

const PageIntro = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <header className="content__header mb-4">
    <div>
      <h1 className="page-header__title">{title}</h1>
      <p className="page-header__lead">{description}</p>
    </div>
  </header>
);

const PaymentMethodsPage: React.FC<{ user: User }> = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [enabled, setEnabled] = useState<ManagedPaymentMethod[]>([
    "stripe",
    "ves",
  ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPlatformPaymentMethods(user)
      .then((data) => {
        if (!cancelled) setEnabled(data.enabled);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Could not load payment methods."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const toggle = (method: ManagedPaymentMethod, on: boolean) => {
    setEnabled((prev) => {
      if (on) {
        return prev.includes(method) ? prev : [...prev, method];
      }
      return prev.filter((m) => m !== method);
    });
    setSuccess(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (enabled.length === 0) {
      setError("Keep at least one method on (Stripe or VES).");
      return;
    }
    setSaving(true);
    try {
      const saved = await updatePlatformPaymentMethods(user, enabled);
      setEnabled(saved.enabled);
      setSuccess("Payment methods saved. Organizations will only see the methods that are on.");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not save payment methods."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageIntro
        title="Payment methods"
        description="Choose which billing rails organizations can use for MayorX plans and Intelligence. Binance Pay is not offered until a merchant account is ready."
      />
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      <Card className="shadow-sm">
        <Card.Header className="border-0">
          <Card.Title className="mb-0">Available methods</Card.Title>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted">
              <Spinner animation="border" size="sm" />
              <span>Loading…</span>
            </div>
          ) : (
            <Form onSubmit={handleSave}>
              <div className="d-flex flex-column gap-3 mb-4">
                {(["stripe", "ves"] as ManagedPaymentMethod[]).map((method) => (
                  <div key={method}>
                    <Form.Check
                      type="switch"
                      id={`pm-${method}`}
                      label={METHOD_COPY[method].title}
                      checked={enabled.includes(method)}
                      onChange={(e) => toggle(method, e.target.checked)}
                      disabled={
                        saving ||
                        (enabled.length === 1 && enabled.includes(method))
                      }
                    />
                    <div className="text-muted small ms-4">
                      {METHOD_COPY[method].hint}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-muted small mb-3">
                Per-organization <strong>Set plan</strong> can further restrict
                among the methods that are on here. Turning a method off hides
                it on the paywall and Intelligence, even if an org still has it
                on its allowlist.
              </p>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </Form>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default PaymentMethodsPage;
